import { buildService } from '@rd-web-markets/shared/dist/services/service';
import serviceMethods from '@rd-web-markets/shared/dist/services/service_methods';
import { replaceInArray } from '@rd-web-markets/shared/dist/util/arrayUtils/arrayUtils';
import { useShowToast } from '@rd-web-markets/shared/dist/util/toast/useToast';
import { useCallback, useEffect, useMemo, useState } from 'react';
const api = buildService('/claims/{id}/claim_reduction_rates', {
  only: ['all', 'get', 'create', 'update', 'delete']
});
const CLAIM_REDUCTION_RATE_POLL_INTERVAL_MS = 7000;

// Fetches all claim reduction rates for the selected claim and cost category.
const useFetchAll = _ref => {
  let {
    claimId,
    setLoading,
    initialQueryParams,
    cost_category_type
  } = _ref;
  const [claimReductionRates, setClaimReductionRates, fetchClaimReductionRates, totalPages] = serviceMethods.useFetch({
    callback: useCallback(async queryParams => {
      const response = await api.all(claimId, {
        ...queryParams,
        cost_category_type
      });
      return response;
    }, [claimId, cost_category_type]),
    setLoading,
    initialQueryParams,
    paginationResourceField: 'claim_reduction_rates'
  });
  return [claimReductionRates, setClaimReductionRates, fetchClaimReductionRates, totalPages];
};

// Creates a claim reduction rate and updates local list state when provided.
const useCreate = _ref2 => {
  let {
    claimId,
    setLoading,
    setClaimReductionRates,
    cost_category_type
  } = _ref2;
  const createClaimReductionRate = serviceMethods.useCreate({
    callback: useCallback(async claimReductionRate => {
      claimReductionRate.cost_category_type = cost_category_type;
      const createdClaimReductionRate = await api.create(claimId, {
        claim_reduction_rate: claimReductionRate
      });
      if (setClaimReductionRates) {
        setClaimReductionRates(replaceInArray(rate => rate.id === claimReductionRate.id, createdClaimReductionRate));
      }
      return createdClaimReductionRate;
    }, [claimId, cost_category_type, setClaimReductionRates]),
    setLoading
  });
  return createClaimReductionRate;
};

// Updates a claim reduction rate and replaces it in local list state when provided.
const useUpdate = _ref3 => {
  let {
    claimId,
    setLoading,
    setClaimReductionRates
  } = _ref3;
  const updateClaimReductionRate = serviceMethods.useUpdate({
    callback: useCallback(async claimReductionRate => {
      const updatedClaimReductionRate = await api.update(claimId, claimReductionRate.id, {
        claim_reduction_rate: claimReductionRate
      });
      if (setClaimReductionRates) {
        setClaimReductionRates(replaceInArray(rate => rate.id === updatedClaimReductionRate.id, updatedClaimReductionRate));
      }
      return updatedClaimReductionRate;
    }, [claimId, setClaimReductionRates]),
    setLoading
  });
  return updateClaimReductionRate;
};

// Deletes a claim reduction rate and removes it from local list state when provided.
const useDelete = _ref4 => {
  let {
    claimId,
    setLoading,
    setClaimReductionRates,
    onSuccess
  } = _ref4;
  const deleteClaimReductionRate = serviceMethods.useDelete({
    onSuccess: onSuccess,
    callback: useCallback(async deletedClaimReductionRate => {
      const promise = await api.delete(claimId, deletedClaimReductionRate.id);
      if (setClaimReductionRates) {
        setClaimReductionRates(claimReductionRates => claimReductionRates.filter(rate => rate.id !== deletedClaimReductionRate.id));
      }
      return promise;
    }, [claimId, setClaimReductionRates]),
    setLoading
  });
  return deleteClaimReductionRate;
};
const useFetch = _ref5 => {
  let {
    claimId,
    claimReductionRateId,
    costCategoryType,
    fetchImmediately = true,
    onSuccess,
    overrideHttpStatusErrorHandlers
  } = _ref5;
  const [claimReductionRate, setClaimReductionRate, fetchClaimReductionRate] = serviceMethods.useFetch({
    callback: useCallback(async () => await api.get(claimId, claimReductionRateId, {
      cost_category_type: costCategoryType
    }), [claimId, claimReductionRateId, costCategoryType]),
    fetchImmediately,
    onSuccess,
    overrideHttpStatusErrorHandlers
  });
  return [claimReductionRate, setClaimReductionRate, fetchClaimReductionRate];
};
const usePollClaimReductionRate = _ref6 => {
  let {
    claimId,
    costCategoryType,
    setClaimReductionRate
  } = _ref6;
  const showToast = useShowToast();
  const [polledClaimReductionRateId, setPolledClaimReductionRateId] = useState(null);
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(() => () => false);
  const overrideHttpStatusErrorHandlers = useMemo(() => ({
    404: () => {
      setClaimReductionRate(oldClaimReductionRate => {
        if (oldClaimReductionRate.set_for_destruction) {
          showToast({
            message: 'Claim reduction rate has been removed successfully!',
            variant: 'success'
          });
        }
        return {
          id: oldClaimReductionRate.id,
          deleted: true
        };
      });
      setIsPollingEnabled(false);
    }
  }), [setClaimReductionRate, showToast]);
  const [,, fetchClaimReductionRate] = useFetch({
    claimId,
    claimReductionRateId: polledClaimReductionRateId,
    costCategoryType: costCategoryType,
    fetchImmediately: false,
    onSuccess: fetchedClaimReductionRate => {
      setClaimReductionRate(fetchedClaimReductionRate);
      setIsPollingEnabled(shouldPoll(fetchedClaimReductionRate));
    },
    overrideHttpStatusErrorHandlers
  });
  serviceMethods.usePolling({
    callback: fetchClaimReductionRate,
    enabled: isPollingEnabled,
    intervalMs: CLAIM_REDUCTION_RATE_POLL_INTERVAL_MS
  });
  const stopPolling = () => {
    setShouldPoll(() => false);
    setIsPollingEnabled(false);
  };
  const startPolling = _ref7 => {
    let {
      claimReductionRateId,
      shouldPollCallback
    } = _ref7;
    setPolledClaimReductionRateId(claimReductionRateId);
    setShouldPoll(() => shouldPollCallback);
    setIsPollingEnabled(true);
  };
  return {
    startPolling,
    stopPolling
  };
};
const claimReductionRatesService = {
  useCrud: _ref8 => {
    let {
      claimId,
      cost_category_type,
      initialQueryParams,
      setLoading,
      onDeleteSuccess = () => {}
    } = _ref8;
    const [claimReductionRates, setClaimReductionRates, fetchClaimReductionRates, totalPages] = useFetchAll({
      claimId,
      initialQueryParams,
      setLoading,
      cost_category_type
    });
    const createClaimReductionRate = useCreate({
      claimId,
      cost_category_type,
      setLoading
    });
    const updateClaimReductionRate = useUpdate({
      claimId,
      cost_category_type,
      setLoading
    });
    const deleteClaimReductionRate = useDelete({
      claimId,
      cost_category_type,
      setLoading,
      onSuccess: onDeleteSuccess
    });
    return {
      claimReductionRates,
      setClaimReductionRates,
      fetchClaimReductionRates,
      createClaimReductionRate,
      updateClaimReductionRate,
      deleteClaimReductionRate,
      totalPages
    };
  },
  useFetchAll,
  useUpdate,
  useDelete,
  useCreate,
  usePollClaimReductionRate
};
export default claimReductionRatesService;